import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Toast from '../../../components/Common/Toast';
import ConfirmModal from '../../../components/Common/ConfirmModal';

const Mesas = () => {
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  // Datos de ejemplo (demo). Luego se conectará a API.
  const [pisoRows, setPisoRows] = useState(() => [
    { id: 1, piso: 1, nombreZona: 'Planta baja', mesas: 12, tieneBarra: false },
    { id: 2, piso: 2, nombreZona: 'Planta alta', mesas: 18, tieneBarra: true },
  ]);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [rowToDeleteId, setRowToDeleteId] = useState(null);

  const [editingRow, setEditingRow] = useState(null);

  const [formPiso, setFormPiso] = useState(3);
  const [formNombreZona, setFormNombreZona] = useState('');
  const [formMesas, setFormMesas] = useState(10);
  const [formTieneBarra, setFormTieneBarra] = useState(false);

  const totals = useMemo(() => {
    const totalMesas = pisoRows.reduce((s, r) => s + Number(r.mesas || 0), 0);
    const zonasConBarra = pisoRows.filter((r) => !!r.tieneBarra).length;
    return { totalMesas, zonasConBarra };
  }, [pisoRows]);

  const resetForm = () => {
    setFormPiso(3);
    setFormNombreZona('');
    setFormMesas(10);
    setFormTieneBarra(false);
  };

  const openAddModal = () => {
    resetForm();
    setEditingRow(null);
    setIsAddModalOpen(true);
  };

  const openEditModal = (row) => {
    setEditingRow(row);
    setFormPiso(row.piso);
    setFormNombreZona(row.nombreZona || '');
    setFormMesas(row.mesas);
    setFormTieneBarra(!!row.tieneBarra);
    setIsEditModalOpen(true);
  };

  const upsertRow = () => {
    const pisoNum = String(formPiso || '').trim();
    if (!pisoNum) {
      setToast({ isOpen: true, message: 'Indique el número de zona', type: 'error' });
      return;
    }
    const nombreZona = String(formNombreZona || '').trim();
    if (!nombreZona) {
      setToast({ isOpen: true, message: 'Indique el nombre de la zona', type: 'error' });
      return;
    }
    const mesasNum = Math.max(0, Number(formMesas || 0));

    if (editingRow) {
      const id = editingRow.id;
      setPisoRows((prev) =>
        prev.map((r) =>
          r.id === id
            ? { ...r, piso: pisoNum, nombreZona, mesas: mesasNum, tieneBarra: !!formTieneBarra }
            : r
        )
      );
      setIsEditModalOpen(false);
      setEditingRow(null);
      setToast({ isOpen: true, message: 'Zona actualizada', type: 'success' });
      return;
    }

    setPisoRows((prev) => [
      ...prev,
      {
        id: prev.length ? Math.max(...prev.map((x) => x.id)) + 1 : 1,
        piso: pisoNum,
        nombreZona,
        mesas: mesasNum,
        tieneBarra: !!formTieneBarra,
      },
    ]);
    setIsAddModalOpen(false);
    setToast({ isOpen: true, message: 'Zona agregada', type: 'success' });
  };

  const requestDelete = (rowId) => {
    setRowToDeleteId(rowId);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (rowToDeleteId == null) return;
    setPisoRows((prev) => prev.filter((r) => r.id !== rowToDeleteId));
    setIsDeleteModalOpen(false);
    setRowToDeleteId(null);
    setToast({ isOpen: true, message: 'Zona eliminada', type: 'success' });
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <nav className="text-sm text-gray-600 mb-4">
        <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
        <span className="mx-2">/</span>
        <Link to="/restaurantes" className="hover:text-teal-600">Restaurantes</Link>
        <span className="mx-2">/</span>
        <span className="text-gray-800">Configuración</span>
        <span className="mx-2">/</span>
        <span className="text-gray-800">Mesas</span>
      </nav>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Clasificación de mesas</h1>
          <p className="text-gray-600 text-sm mt-1">
            Defina las zonas del local, cuántas mesas habrá por zona y si la zona cuenta con barra.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="px-3 py-2 rounded-md border border-gray-200 bg-white">
            <div className="text-xs text-gray-500">Total mesas</div>
            <div className="font-semibold text-gray-900">{totals.totalMesas}</div>
          </div>
          <div className="px-3 py-2 rounded-md border border-gray-200 bg-white">
            <div className="text-xs text-gray-500">Zonas con barra</div>
            <div className="font-semibold text-gray-900">{totals.zonasConBarra}</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
        <div className="px-4 py-3 bg-teal-50 border-b border-gray-200">
          <h2 className="font-semibold text-teal-800">Zonas</h2>
        </div>

        <div className="p-4">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="text-sm text-gray-600">
              Configure las zonas y su capacidad por zona.
            </div>
            <button
              type="button"
              onClick={openAddModal}
              className="px-4 py-2 bg-teal-600 text-white rounded-md text-sm font-medium hover:bg-teal-700"
            >
              + Agregar zona
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Zona</th>
                  <th className="text-left px-4 py-3 font-medium">Nombre</th>
                  <th className="text-left px-4 py-3 font-medium">Mesas por zona</th>
                  <th className="text-left px-4 py-3 font-medium">¿Tiene barra?</th>
                  <th className="text-left px-4 py-3 font-medium w-[160px]">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pisoRows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm text-teal-700">{row.piso}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">{row.nombreZona || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">{row.mesas}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={row.tieneBarra ? 'text-teal-700 font-medium' : 'text-gray-600'}>
                        {row.tieneBarra ? 'Sí' : 'No'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => openEditModal(row)}
                          className="text-sm text-teal-700 hover:text-teal-900 font-medium"
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => requestDelete(row.id)}
                          className="text-sm text-red-600 hover:text-red-700 font-medium"
                        >
                          Quitar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {pisoRows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                      No hay zonas configuradas.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal Agregar */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50">
          <div
            className="bg-white rounded-lg shadow-xl max-w-xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">Agregar zona</h3>
              <button
                type="button"
                onClick={() => setIsAddModalOpen(false)}
                className="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Zona</label>
                <input
                  type="number"
                  min={0}
                  value={formPiso}
                  onChange={(e) => setFormPiso(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de la zona</label>
                  <input
                    type="text"
                    value={formNombreZona}
                    onChange={(e) => setFormNombreZona(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    placeholder="Ej. Planta baja, Terraza, VIP..."
                  />
                </div>
              <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mesas por zona</label>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={formMesas}
                  onChange={(e) => setFormMesas(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formTieneBarra}
                    onChange={(e) => setFormTieneBarra(e.target.checked)}
                  />
                    <span className="text-sm text-gray-700">Tiene barra en esta zona</span>
                </label>
              </div>
              <p className="text-xs text-gray-500">
                Demo: por ahora se guarda en memoria. Próximamente se conectará a la API.
              </p>
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsAddModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={upsertRow}
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-md transition-colors"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar */}
      {isEditModalOpen && editingRow && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50">
          <div
            className="bg-white rounded-lg shadow-xl max-w-xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">Editar zona</h3>
              <button
                type="button"
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingRow(null);
                }}
                className="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Zona</label>
                <input
                  type="number"
                  min={0}
                  value={formPiso}
                  onChange={(e) => setFormPiso(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de la zona</label>
                <input
                  type="text"
                  value={formNombreZona}
                  onChange={(e) => setFormNombreZona(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="Ej. Planta baja, Terraza, VIP..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mesas por zona</label>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={formMesas}
                  onChange={(e) => setFormMesas(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formTieneBarra}
                    onChange={(e) => setFormTieneBarra(e.target.checked)}
                  />
                  <span className="text-sm text-gray-700">Tiene barra en esta zona</span>
                </label>
              </div>
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingRow(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={upsertRow}
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-md transition-colors"
              >
                Guardar cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmación de eliminación */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setRowToDeleteId(null);
        }}
        onConfirm={confirmDelete}
        title="Eliminar zona"
        message="¿Seguro que deseas eliminar esta zona? Esto eliminará su configuración de mesas."
        confirmText="Eliminar"
        cancelText="Cancelar"
      />

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default Mesas;

